import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Zap, Package, Server, CreditCard, Truck, Lock, RefreshCcw, Activity } from 'lucide-react';
import { ClearinghouseCard } from '../components/integrations/ClearinghouseCard';
import { SupplierCard } from '../components/integrations/SupplierCard';
import { clearinghouses, suppliers } from '../components/integrations/integrationsData';

const HowItWorksStep = ({ step, icon: Icon, title, description, color }) => (
  <div className="relative text-center p-8">
    <div className="text-7xl font-extrabold text-gray-100 absolute top-2 left-1/2 -translate-x-1/2">{step}</div>
    <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${color === 'blue' ? 'bg-[#014DB7]' : 'bg-[#84CC16]'}`}>
      <Icon className="w-8 h-8 text-white" />
    </div>
    <h3 className="text-xl font-bold text-[#0A1628] mb-3">{title}</h3>
    <p className="text-[#64748B]">{description}</p>
  </div>
);

export const IntegrationsPage = () => (
  <div className="min-h-screen pt-48">
    <IntegrationsHero />
    <ClearinghouseSection />
    <SupplierSection />
    <HowItWorksSection />
    <IntegrationsCTA />
  </div>
);

const IntegrationsHero = () => (
  <section className="py-20 px-6 lg:px-8 gradient-animated relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern opacity-50" />
    <div className="relative max-w-7xl mx-auto text-center stagger-children">
      <div className="badge-blue mx-auto mb-6">
        <Server className="w-4 h-4" />
        <span>Integrations and Suppliers</span>
      </div>
      <h1 className="text-display text-[#0A1628] mb-6">
        Connected <span className="gradient-text-mixed">Ecosystem</span>
      </h1>
      <p className="text-subtitle max-w-3xl mx-auto mb-10">
        Seamless integrations with leading clearinghouses, RCM platforms, and 11 DME suppliers —
        all pre-configured and ready for your API keys.
      </p>
      <div className="flex flex-wrap justify-center gap-6">
        <div className="flex items-center gap-2 text-[#014DB7] font-semibold">
          <Shield className="w-5 h-5" />
          <span>3 Clearinghouse Integrations</span>
        </div>
        <div className="flex items-center gap-2 text-[#84CC16] font-semibold">
          <Package className="w-5 h-5" />
          <span>11 DME Suppliers</span>
        </div>
        <div className="flex items-center gap-2 text-[#0A1628] font-semibold">
          <Zap className="w-5 h-5" />
          <span>Ready for API Keys</span>
        </div>
      </div>
    </div>
  </section>
);

const ClearinghouseSection = () => (
  <section className="py-24 px-6 lg:px-8 bg-white relative">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-5 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-[#014DB7] flex items-center justify-center shadow-glow-blue">
          <CreditCard className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-[#0A1628]">Clearinghouse and RCM Integrations</h2>
          <p className="text-[#64748B]">Revenue cycle management and eligibility verification</p>
        </div>
      </div>
      <p className="text-[#64748B] max-w-3xl mb-12 leading-relaxed">
        Connect with the industry's top clearinghouses and revenue cycle management platforms.
        Verify patient eligibility in real-time, submit claims electronically, manage denials,
        and process remittances — all from within your DME R'US dashboard.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {clearinghouses.map((item, i) => (
          <ClearinghouseCard key={i} item={item} />
        ))}
      </div>
    </div>
  </section>
);

const SupplierSection = () => (
  <section className="py-24 px-6 lg:px-8 bg-[#F8FAFC] relative">
    <div className="absolute inset-0 grid-pattern opacity-40" />
    <div className="relative max-w-7xl mx-auto">
      <div className="flex items-center gap-5 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-[#84CC16] flex items-center justify-center shadow-glow-lime">
          <Truck className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-[#0A1628]">DME Supplier Network</h2>
          <p className="text-[#64748B]">11 suppliers pre-configured for product linking and order routing</p>
        </div>
      </div>
      <p className="text-[#64748B] max-w-3xl mb-12 leading-relaxed">
        Our supplier network includes the nation's leading DME manufacturers, distributors, and specialty providers.
        Each supplier is pre-configured with API endpoints for product linking and order routing.
        Connect your supplier accounts to enable automated order fulfillment.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-grid">
        {suppliers.map((item, i) => (
          <SupplierCard key={i} item={item} />
        ))}
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="py-24 px-6 lg:px-8 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
          How <span className="gradient-text-blue">Integration</span> Works
        </h2>
        <p className="text-[#64748B] max-w-2xl mx-auto">Get connected in three simple steps. No complex setup required.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <HowItWorksStep step="01" icon={Lock} title="Add Your API Keys" description="Enter your clearinghouse or supplier API credentials in the Super Admin settings panel. All keys are encrypted at rest." color="blue" />
        <HowItWorksStep step="02" icon={RefreshCcw} title="Auto-Configure" description="DME R'US automatically configures the integration, tests the connection, and syncs your account data." color="lime" />
        <HowItWorksStep step="03" icon={Activity} title="Start Processing" description="Verify eligibility, submit claims, route orders, and track everything from your unified dashboard." color="blue" />
      </div>
    </div>
  </section>
);

const IntegrationsCTA = () => (
  <section className="py-24 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#014DB7] via-[#003A8C] to-[#0A1628]" />
    <div className="absolute inset-0 grid-pattern opacity-10" />
    <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
        Ready to Connect Your <span className="text-[#84CC16]">Integrations?</span>
      </h2>
      <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
        Get started with DME R'US and connect your clearinghouse and supplier accounts in minutes.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/demo">
          <Button className="btn-lift bg-[#84CC16] text-[#0A1628] hover:bg-[#A3E635] rounded-xl px-10 py-6 font-bold text-lg shadow-glow-lime group">
            Request a Demo
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
        <Link to="/contact">
          <Button variant="outline" className="btn-lift border-2 border-white/30 text-white hover:bg-white/10 rounded-xl px-10 py-6 font-bold text-lg">
            Talk to Sales
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export default IntegrationsPage;
